import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useTransitionFindings } from "@/components/experience/use-transition-findings";
import type { TriageAction } from "@/components/experience/triage-action-dialog";

interface UseWorkspaceMutationsOptions {
  projectId: string;
  walkthroughId: string | undefined;
  trackerTarget: any;
  hasLinkedRepo: boolean;
  walkthrough: any;
  clearSelection: () => void;
}

export function useWorkspaceMutations({
  projectId,
  walkthroughId,
  trackerTarget,
  hasLinkedRepo,
  walkthrough,
  clearSelection,
}: UseWorkspaceMutationsOptions) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const transition = useTransitionFindings(projectId);

  const [actionDialog, setActionDialog] = useState<{
    action: TriageAction;
    ids: string[];
    subject?: string;
  } | null>(null);
  const [createIssueCanonicalId, setCreateIssueCanonicalId] = useState<
    string | null
  >(null);
  const [linkExistingCanonicalId, setLinkExistingCanonicalId] = useState<
    string | null
  >(null);
  const [bulkIssuesOpen, setBulkIssuesOpen] = useState(false);
  const [bulkComparisonCanonicalIds, setBulkComparisonCanonicalIds] = useState<
    string[] | null
  >(null);

  const createIssueMutation = trpc.finding.createIssue.useMutation({
    onSuccess: (data) => {
      const dataProv = (data as { provider?: string }).provider;
      const prov =
        dataProv ??
        (trackerTarget?.provider ? trackerTarget.provider : "github");
      const provName =
        prov === "linear"
          ? "Linear"
          : prov === "jira"
            ? "Jira"
            : prov === "azure_devops"
              ? "Azure DevOps"
              : "GitHub";
      toast.success(`${provName} issue created`, {
        action: data.issueUrl
          ? {
              label: "View issue",
              onClick: () => window.open(data.issueUrl, "_blank"),
            }
          : undefined,
      });
      utils.walkthrough.getById.invalidate({ id: walkthroughId ?? "" });
      utils.finding.list.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Issue creation failed"),
  });

  const rerunMutation = trpc.walkthrough.autoRun.useMutation({
    onSuccess: (data) => {
      toast.success("Walkthrough re-run started");
      navigate(`/projects/${projectId}/walkthroughs/${data.walkthroughId}`);
    },
    onError: (err) => {
      if (walkthrough?.scenarioId) {
        navigate(`/projects/${projectId}/scenarios/${walkthrough.scenarioId}`);
      } else {
        toastTrpcError(err, "Failed to start re-run");
      }
    },
  });

  const handleBulkReopen = useCallback(
    (selectedIds: Set<string>) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      transition.run(
        { ids, to: "open", reason: "reopened" },
        `Reopened ${ids.length} findings`,
        clearSelection,
      );
    },
    [transition, clearSelection],
  );

  return {
    actionDialog,
    setActionDialog,
    createIssueCanonicalId,
    setCreateIssueCanonicalId,
    linkExistingCanonicalId,
    setLinkExistingCanonicalId,
    bulkIssuesOpen,
    setBulkIssuesOpen,
    bulkComparisonCanonicalIds,
    setBulkComparisonCanonicalIds,
    createIssueMutation,
    rerunMutation,
    handleBulkReopen,
    isTransitionPending: transition.isPending,
  };
}
